import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check, ArrowDown, Home as HomeIcon, Building2 } from "lucide-react";
import { useData } from "@/context/DataContext";
import Seo from "@/components/site/Seo";
import { Reveal, MaskedLines } from "@/components/site/Reveal";
import { WhatsAppButton } from "@/components/site/WhatsButton";
import ServiceIcon from "@/components/site/ServiceIcon";
import BeforeAfterSlider from "@/components/site/BeforeAfterSlider";
import Marquee from "@/components/site/Marquee";
import ReviewsCarousel from "@/components/site/ReviewsCarousel";
import { resolveImg } from "@/lib/api";

const HERO = "https://images.pexels.com/photos/29821815/pexels-photo-29821815.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1800";

const STRENGTHS = ["Sopralluoghi gratuiti", "Preventivi senza impegno", "Servizi per privati e aziende", "Interventi personalizzati"];
const METHOD = [
  { t: "Primo contatto", d: "Ci racconti le tue esigenze per telefono, su WhatsApp o tramite il modulo." },
  { t: "Sopralluogo gratuito", d: "Valutiamo l'area e le lavorazioni necessarie, senza impegno." },
  { t: "Preventivo personalizzato", d: "Prepariamo una proposta chiara e su misura per il tuo verde." },
  { t: "Esecuzione dell'intervento", d: "Realizziamo il lavoro con cura, ordine e attenzione ai dettagli." },
];

export default function Home() {
  const { settings, services, works, beforeAfter, reviews } = useData();
  const reduce = useReducedMotion();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, reduce ? 1 : 1.12]);

  if (!settings) return <div className="min-h-screen bg-av-black" />;

  const schema = {
    "@context": "https://schema.org",
    "@type": "HomeAndConstructionBusiness",
    name: "Animaverde",
    slogan: settings.slogan,
    description: settings.hero_sottotitolo,
    telephone: settings.telefono_link,
    email: settings.email,
    url: `https://${settings.sito}`,
    areaServed: "Provincia di Varese",
    address: { "@type": "PostalAddress", streetAddress: "Via Garibaldi, 143/1", addressLocality: "Ferno", postalCode: "21010", addressRegion: "VA", addressCountry: "IT" },
    sameAs: settings.facebook ? [settings.facebook] : undefined,
  };

  return (
    <>
      <Seo title="Cura e passione per il tuo verde" description={settings.hero_sottotitolo} path="/" schema={schema} />

      {/* HERO */}
      <section ref={heroRef} className="relative h-[100svh] min-h-[640px] w-full overflow-hidden bg-av-black">
        <motion.div className="absolute inset-0 z-0" style={{ y: bgY, scale }}>
          <img src={HERO} alt="Giardino curato professionalmente" className="w-full h-full object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-av-black via-av-black/45 to-av-black/60" />
        <div className="absolute inset-0 grain" />

        <div className="relative z-10 h-full mx-auto max-w-[1400px] px-5 sm:px-8 flex flex-col justify-end pb-16 sm:pb-24">
          <motion.p
            className="overline text-av-lime mb-6"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15, duration: 0.8 }}
          >
            Giardinaggio & cura del verde — Provincia di Varese
          </motion.p>

          <h1 className="font-display text-[13vw] leading-[0.92] sm:text-7xl lg:text-8xl tracking-tight text-av-white max-w-5xl">
            <MaskedLines
              lines={["Cura e passione", <>per il tuo <span className="italic text-av-lime">verde</span></>]}
              delay={0.2}
            />
          </h1>

          <motion.p
            className="mt-7 max-w-2xl text-av-white/85 text-base sm:text-lg leading-relaxed"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }}
          >
            {settings.hero_sottotitolo}
          </motion.p>

          <motion.div
            className="mt-9 flex flex-wrap gap-3"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8 }}
          >
            <Link
              to="/contatti"
              data-testid="hero-cta-sopralluogo"
              className="inline-flex items-center gap-2 rounded-full bg-av-bright text-av-black px-7 py-3.5 text-sm font-semibold hover:bg-av-lime transition-colors duration-300"
            >
              Richiedi un sopralluogo gratuito <ArrowUpRight size={17} strokeWidth={2.5} />
            </Link>
            <WhatsAppButton testId="hero-whatsapp" />
          </motion.div>
        </div>

        <motion.div
          className="absolute bottom-6 right-6 z-10 text-av-white/60 hidden sm:block"
          animate={reduce ? {} : { y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 2.2 }}
        >
          <ArrowDown size={22} />
        </motion.div>
      </section>

      {/* STRENGTHS STRIP */}
      <section className="bg-av-green-dark border-y border-white/10" style={{ backgroundColor: "var(--av-green-dark)" }}>
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/10">
          {STRENGTHS.map((s, i) => (
            <Reveal key={s} delay={i * 0.08} className="flex items-center gap-3 py-6 px-4 sm:px-6">
              <Check size={18} className="text-av-lime shrink-0" strokeWidth={3} />
              <span className="text-sm sm:text-[15px] font-medium text-av-white">{s}</span>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PRESENTATION */}
      <section className="relative bg-av-white text-av-black py-24 sm:py-32">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <p className="overline text-av-green mb-5" style={{ color: "var(--av-green)" }}>Chi è Animaverde</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.03] tracking-tight text-balance">
              {settings.presentazione_titolo}
            </h2>
            <p className="mt-7 text-neutral-600 text-lg leading-relaxed max-w-xl">{settings.presentazione_testo}</p>
            <Link
              to="/chi-siamo"
              data-testid="presentation-about-link"
              className="mt-9 inline-flex items-center gap-2 text-sm font-semibold text-av-green group"
              style={{ color: "var(--av-green)" }}
            >
              Scopri chi siamo
              <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative overflow-hidden rounded-3xl aspect-[4/5]">
              <img
                src="/operatore-animaverde.jpg"
                alt="Operatore Animaverde al lavoro con il decespugliatore"
                className="w-full h-full object-cover" loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVICES */}
      <section className="relative bg-av-black py-24 sm:py-32 grain">
        <div className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
            <Reveal>
              <p className="overline text-av-bright mb-4">I nostri servizi</p>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-av-white max-w-2xl text-balance">
                Tutto ciò di cui il tuo verde ha bisogno
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/servizi" className="inline-flex items-center gap-2 text-sm font-semibold text-av-lime whitespace-nowrap">
                Tutti i servizi <ArrowRight size={16} />
              </Link>
            </Reveal>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 rounded-3xl overflow-hidden border border-white/10">
            {services.map((s, i) => (
              <Reveal key={s.id} delay={(i % 3) * 0.08}>
                <Link
                  to={`/servizi#${s.slug}`}
                  data-testid={`service-card-${s.slug}`}
                  className="group relative block h-full bg-av-black hover:bg-av-green-dark transition-colors duration-500 p-8 sm:p-10"
                >
                  <ServiceIcon name={s.icona} size={30} className="text-av-bright group-hover:text-av-lime transition-colors" strokeWidth={1.5} />
                  <h3 className="mt-6 font-display text-2xl text-av-white">{s.titolo}</h3>
                  <p className="mt-3 text-sm text-av-gray leading-relaxed">{s.breve}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-av-lime opacity-80 group-hover:gap-3 transition-all">
                    Scopri di più <ArrowUpRight size={15} />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRIVATI / AZIENDE */}
      <section className="grid md:grid-cols-2">
        <SplitCard
          icon={<HomeIcon size={26} />}
          overline="Per privati"
          title="Giardini che raccontano la tua casa"
          items={["Giardini di abitazioni", "Ville", "Cortili", "Spazi verdi residenziali"]}
          img="https://images.unsplash.com/photo-1633330948542-0b3bdeefcdb3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          dark
        />
        <SplitCard
          icon={<Building2 size={26} />}
          overline="Per aziende"
          title="Spazi verdi che curano la tua immagine"
          items={["Aree verdi aziendali", "Spazi esterni", "Manutenzioni programmate", "Cura dell'immagine esterna dell'attività"]}
          img="https://images.unsplash.com/photo-1668870464207-0f85e787b7fe?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
        />
      </section>

      {/* BEFORE / AFTER */}
      {beforeAfter[0] && (
        <section className="bg-av-white text-av-black py-24 sm:py-32">
          <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
            <div className="grid lg:grid-cols-[0.9fr_1.4fr] gap-12 items-center">
              <Reveal>
                <p className="overline mb-4" style={{ color: "var(--av-green)" }}>Prima e dopo</p>
                <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-balance">Il verde trasformato, davanti ai tuoi occhi</h2>
                <p className="mt-6 text-neutral-600 leading-relaxed">Trascina il cursore per vedere la differenza tra il prima e il dopo dei nostri interventi dimostrativi.</p>
                <Link to="/prima-e-dopo" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--av-green)" }}>
                  Vedi tutti i confronti <ArrowRight size={16} />
                </Link>
              </Reveal>
              <Reveal delay={0.15}>
                <BeforeAfterSlider prima={beforeAfter[0].prima} dopo={beforeAfter[0].dopo} testId="home-before-after" />
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* RECENT WORKS */}
      <section className="bg-av-black py-24 sm:py-32 grain">
        <div className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
            <Reveal>
              <p className="overline text-av-bright mb-4">Lavori recenti</p>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-av-white text-balance">Una selezione dei nostri interventi</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/lavori" className="inline-flex items-center gap-2 text-sm font-semibold text-av-lime whitespace-nowrap">
                Tutti i lavori <ArrowRight size={16} />
              </Link>
            </Reveal>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {works.slice(0, 3).map((w, i) => (
              <Reveal key={w.id} delay={i * 0.08}>
                <article data-testid={`home-work-${i}`} className="group">
                  <div className="relative overflow-hidden rounded-2xl aspect-[4/3]">
                    <img src={resolveImg(w.immagine)} alt={w.alt || w.titolo} loading="lazy"
                         className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <span className="absolute top-3 left-3 rounded-full bg-av-black/70 backdrop-blur px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-av-lime">{w.categoria}</span>
                  </div>
                  <div className="mt-4">
                    <div className="flex items-center gap-2 text-xs text-av-gray">
                      <span>{w.comune}</span>
                    </div>
                    <h3 className="mt-1 font-display text-xl text-av-white">{w.titolo}</h3>
                    <p className="mt-2 text-sm text-av-gray line-clamp-2">{w.descrizione}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* METHOD */}
      <section className="bg-av-green-dark py-24 sm:py-32" style={{ backgroundColor: "var(--av-green-deep)" }}>
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <Reveal><p className="overline text-av-lime mb-4">Come lavoriamo</p></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-av-white mb-16 max-w-3xl text-balance">Un metodo semplice, dal primo contatto al risultato</h2>
          </Reveal>
          <div className="space-y-0">
            {METHOD.map((m, i) => (
              <Reveal key={m.t} delay={i * 0.06}>
                <div className="grid md:grid-cols-[auto_1fr_1.4fr] gap-4 md:gap-10 items-baseline py-8 border-t border-white/15 group">
                  <span className="font-display text-5xl md:text-7xl text-av-white/20 group-hover:text-av-bright transition-colors duration-500">0{i + 1}</span>
                  <h3 className="font-display text-2xl md:text-3xl text-av-white">{m.t}</h3>
                  <p className="text-av-gray leading-relaxed">{m.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-av-black py-14 border-y border-white/10">
        <Marquee items={["Manutenzione giardini", "Potature", "Abbattimenti", "Trattamenti fitosanitari", "Nuovi giardini", "Rifacimento giardini"]} />
      </section>

      {/* REVIEWS */}
      <section className="bg-av-white text-av-black py-24 sm:py-28">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <Reveal className="text-center mb-14">
            <p className="overline mb-4" style={{ color: "var(--av-green)" }}>Dicono di noi</p>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-neutral-900">Le recensioni dei nostri clienti</h2>
          </Reveal>
          {reviews && reviews.length > 0 ? (
            <Reveal><ReviewsCarousel /></Reveal>
          ) : (
            <h3 className="font-display text-2xl sm:text-3xl tracking-tight max-w-2xl mx-auto text-center text-neutral-500">
              {settings.recensioni_placeholder}
            </h3>
          )}
        </div>
      </section>

      {/* FINAL CTA */}
      <FinalCta settings={settings} />
    </>
  );
}

function SplitCard({ icon, overline, title, items, img, dark }) {
  return (
    <div className={`relative overflow-hidden ${dark ? "bg-av-green-dark" : "bg-av-black"}`} style={dark ? { backgroundColor: "var(--av-green-dark)" } : {}}>
      <div className="absolute inset-0 opacity-25">
        <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-av-black/90 to-av-black/40" />
      <Reveal className="relative z-10 p-10 sm:p-14 min-h-[420px] flex flex-col justify-end">
        <div className="w-12 h-12 rounded-full bg-av-bright/15 text-av-lime flex items-center justify-center mb-6">{icon}</div>
        <p className="overline text-av-lime mb-3">{overline}</p>
        <h3 className="font-display text-3xl sm:text-4xl text-av-white mb-6">{title}</h3>
        <ul className="space-y-2">
          {items.map((it) => (
            <li key={it} className="flex items-center gap-3 text-av-white/85 text-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-av-lime" /> {it}
            </li>
          ))}
        </ul>
      </Reveal>
    </div>
  );
}

function FinalCta({ settings }) {
  return (
    <section className="relative bg-av-black py-24 sm:py-32 overflow-hidden">
      <div className="absolute inset-0 opacity-30">
        <img src="https://images.unsplash.com/photo-1686663048931-6df69f577a2f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="" className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="absolute inset-0 bg-av-black/70" />
      <div className="relative z-10 mx-auto max-w-[1000px] px-5 sm:px-8 text-center">
        <Reveal>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-av-white text-balance">Il tuo giardino ha bisogno di un intervento?</h2>
          <p className="mt-6 text-av-gray text-lg max-w-2xl mx-auto">Contattaci per raccontarci le tue esigenze e richiedere un sopralluogo gratuito.</p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <a href={`tel:${settings.telefono_link}`} data-testid="final-cta-call"
               className="inline-flex items-center gap-2 rounded-full bg-av-bright text-av-black px-7 py-3.5 text-sm font-semibold hover:bg-av-lime transition-colors">
              Chiama il {settings.telefono}
            </a>
            <WhatsAppButton testId="final-cta-whatsapp" />
            <Link to="/contatti" data-testid="final-cta-form"
                  className="inline-flex items-center gap-2 rounded-full border border-white/25 text-av-white px-7 py-3.5 text-sm font-semibold hover:border-av-lime hover:text-av-lime transition-colors">
              Compila il modulo <ArrowUpRight size={16} />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
